import classNames from "classnames"
import { format, parseISO } from "date-fns"
import pluralize from "pluralize"
import React, { useContext, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom"

import Badge from "src/components/Badge"
import Button from "src/components/Button"
import Chips from "src/components/Chips"
import DataTableV2 from "src/components/DataTableV2"
import Form from "src/components/Form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"
import Tooltip from "src/components/Tooltip"

import { getApideckSyncRecords, retrySyncRecord } from "src/api/Apideck"

import useDatatable from "src/hooks/use_datatable"
import useDebounce from "src/hooks/use_debounce"
import { useToast } from "src/hooks/use_toast"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { titlecase } from "src/utils/string_helpers"

import { AccountingContext } from "./AccountingView"
import {
  DOCUMENT_TYPE_OPTIONS,
  SYNC_ERROR_CODE_FILTER_OPTIONS,
  SYNC_STATUSES,
  SYNC_STATUS_FILTER_OPTIONS,
  documentTypeLabel,
} from "./constants"

const { EXCLUDED, FAILED } = SYNC_STATUSES
const GUEST = "Guest"
const INACTIVE = "inactive"

const STATUS_BADGE_COLORS = {
  [SYNC_STATUSES.PENDING]: "blue",
  [SYNC_STATUSES.EXCLUDED]: "yellow",
  [SYNC_STATUSES.FAILED]: "red",
  [SYNC_STATUSES.SUCCESS]: "teal",
}

const DATE_FORMAT = "MM/dd/yyyy hh:mm aa"

// Exported for tests: the option list renders in a portal jsdom cannot open.
// A bare label means the counts have not arrived; an explicit (0) means the
// status genuinely has no rows, and those must not look the same.
export const statusOptionLabel = (status, filterOptions) => {
  if (!filterOptions) return titlecase(status)

  return `${titlecase(status)} (${filterOptions.statusCounts?.[status] ?? 0})`
}

const SyncTab = () => {
  const navigate = useNavigate()
  const { integration, marinaSlug, selectedAccountingService } =
    useContext(AccountingContext)
  const queryClient = useQueryClient()
  const showToast = useToast()

  // The two accounting columns follow the connected service: "QuickBooks Ref No." and "QuickBooks ID".
  const serviceName = selectedAccountingService?.name
  const refNoHeader = serviceName
    ? `${serviceName} Ref No.`
    : "Accounting Ref No."
  const externalIdHeader = serviceName ? `${serviceName} ID` : "Accounting ID"

  const table = useDatatable({
    queryKey: ["apideckSyncRecords", marinaSlug],
    fetchFn: (params) => getApideckSyncRecords(marinaSlug, params),
    defaultFilters: {
      search: "",
      documentTypes: [],
      syncStatuses: [],
      syncErrorCodes: [],
    },
    defaultSort: { column: "updatedAt", direction: "desc" },
    enabled: integration.setupComplete,
  })

  const [searchTerm, setSearchTerm] = useState(table.filters.search || "")
  const [debouncedSearch] = useDebounce((value) =>
    table.setFilter("search", value)
  )

  const { documentTypes, syncStatuses, syncErrorCodes } = table.filters
  const filteredCount = table.data?.pagination?.totalCount
  const totalCount = table.data?.filterOptions?.totalCount

  const handleSearch = (event) => {
    setSearchTerm(event.target.value)
    debouncedSearch(event.target.value.trim())
  }

  const clearSearch = () => {
    setSearchTerm("")
    table.setFilter("search", "")
  }

  const searchForReferencedDocument = (referencedDocument) => {
    setSearchTerm(referencedDocument.number)
    // The referenced document rarely shares the held row's type or status, so a Type or Status
    // filter would hide the very row the link points at.
    table.clearAllFilters()
    table.setFilter("search", referencedDocument.number)
  }

  const resetFilters = () => {
    setSearchTerm("")
    table.clearAllFilters()
  }

  const { mutate: retry } = useMutation(
    (row) =>
      retrySyncRecord({
        marinaSlug,
        documentType: row.documentType,
        id: row.id,
      }),
    {
      onSuccess: (data) => {
        const { syncRecord } = data
        showToast(
          `Retry of ${documentTypeLabel(
            syncRecord.documentType
          ).toLowerCase()} ${
            syncRecord.documentNumber || syncRecord.encodedId
          } initiated successfully`,
          { type: "success" }
        )
        // A retry moves the record back to pending, so refetch rather than
        // patching the row — the filtered set itself may have changed.
        queryClient.invalidateQueries(["apideckSyncRecords", marinaSlug])
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
      },
    }
  )

  const wrappingHeader = (text) => (
    <span className="whitespace-normal">{text}</span>
  )

  const renderDate = (value) =>
    value ? format(parseISO(value), DATE_FORMAT) : null

  const renderExternalId = (row) => {
    if (!row.externalId) return null

    const label = <span className="block break-words">{row.externalId}</span>
    if (!row.externalUrl) return label

    return (
      <a
        href={row.externalUrl}
        className="cursor-pointer text-blue-600"
        rel="noreferrer noopener"
        target="_blank"
      >
        {label}
      </a>
    )
  }

  const renderErrors = (row) => {
    const { syncErrors } = row
    if (row.syncStatus !== FAILED || syncErrors.length === 0) return undefined

    const actionable = syncErrors.filter((error) => error.actionable)
    const nonActionable = syncErrors.filter((error) => !error.actionable)
    // Headings only earn their space when both kinds are present — otherwise the chip's code
    // already says which kind this row is, and a lone heading is just noise.
    const grouped = actionable.length > 0 && nonActionable.length > 0
    const bulleted = syncErrors.length > 1
    const lead = actionable[0] || syncErrors[0]
    // Non-actionable copy is generic, so a count would tally lines the marina cannot tell apart.
    const leadCount = lead.actionable
      ? syncErrors
          .filter((error) => error.code === lead.code)
          .reduce((total, error) => total + error.count, 0)
      : 1
    const otherCodeCount =
      new Set(syncErrors.map((error) => error.code)).size - 1
    const referencedDocuments = [
      ...new Map(
        syncErrors
          .filter((error) => error.referencedDocument)
          .map((error) => [
            error.referencedDocument.number,
            error.referencedDocument,
          ])
      ).values(),
    ]

    const errorLines = (errors) =>
      errors.map((error, index) => (
        <div key={index}>{`${bulleted ? "- " : ""}${error.code}: ${
          error.displayMessage
        }`}</div>
      ))

    return (
      <div>
        <Tooltip
          text={
            <div className="space-y-3">
              {grouped ? (
                <>
                  <div>
                    <div className="font-semibold">Action required</div>
                    {errorLines(actionable)}
                  </div>
                  <div>
                    <div className="font-semibold">No action needed</div>
                    {errorLines(nonActionable)}
                  </div>
                </>
              ) : (
                errorLines(syncErrors)
              )}
            </div>
          }
          variant="dark"
          placement="left"
          maxWidth="400px"
          tabIndex={0}
        >
          <div className="flex items-center gap-1">
            <Badge
              color="gray"
              variant="small"
              text={leadCount > 1 ? `${lead.code} ×${leadCount}` : lead.code}
            />
            {otherCodeCount > 0 && (
              <span className="text-xs font-semibold text-gray-600">
                {`+${otherCodeCount}`}
              </span>
            )}
          </div>
        </Tooltip>
        {referencedDocuments.map((document) => (
          <button
            key={document.number}
            type="button"
            className="text-link block w-full break-words text-left text-sm"
            onClick={() => searchForReferencedDocument(document)}
          >
            {`${document.type} ${document.number}`}
          </button>
        ))}
      </div>
    )
  }

  const COLUMNS = [
    {
      key: "syncStatus",
      header: "Status",
      sortable: true,
      cardRole: "badge",
      width: "8%",
      render: (row) => (
        <Badge
          color={STATUS_BADGE_COLORS[row.syncStatus] || "gray"}
          text={titlecase(row.syncStatus)}
        />
      ),
    },
    {
      key: "documentType",
      header: "Type",
      sortable: true,
      cardRole: "subtitle",
      width: "9%",
      render: (row) => documentTypeLabel(row.documentType),
    },
    {
      key: "source",
      header: "Source",
      cardRole: "detail",
      width: "9%",
      render: (row) => row.source,
    },
    {
      key: "encodedId",
      header: "Dockwa ID",
      cardRole: "title",
      width: "9%",
      render: (row) => (
        <a href={row.parentObjectPath} className="cursor-pointer text-blue-600">
          <span className="block break-words">{row.encodedId}</span>
        </a>
      ),
    },
    {
      key: "contact",
      header: "Customer",
      cardRole: "detail",
      width: "9%",
      render: (row) => row.contact?.name || GUEST,
    },
    {
      key: "amount",
      header: "Amount",
      cardRole: "detail",
      width: "8%",
      render: (row) =>
        row.amount == null
          ? null
          : formatCurrencyFromMinorUnits({ amount: row.amount }),
    },
    {
      key: "errors",
      header: wrappingHeader("Sync Error Code"),
      cardRole: "detail",
      width: "12%",
      render: renderErrors,
    },
    {
      key: "documentNumber",
      header: wrappingHeader(refNoHeader),
      cardRole: "detail",
      width: "10%",
      render: (row) => (
        <span className="block break-words">{row.documentNumber}</span>
      ),
    },
    {
      key: "externalId",
      header: wrappingHeader(externalIdHeader),
      cardRole: "detail",
      width: "10%",
      render: renderExternalId,
    },
    {
      key: "updatedAt",
      header: "Updated",
      sortable: true,
      cardRole: "detail",
      width: "8%",
      render: (row) => renderDate(row.updatedAt),
    },
    {
      key: "syncedAt",
      header: "Last Synced",
      hide: true,
      cardRole: "detail",
      width: "8%",
      render: (row) => renderDate(row.syncedAt),
    },
  ]

  const ACTIONS = [
    {
      label: "Retry",
      icon: "rotate-left",
      iconStyle: "solid",
      primary: true,
      onClickSingle: (row) => retry(row),
      disabled: (row) =>
        integration.status === INACTIVE ||
        (row.syncStatus !== FAILED && row.syncStatus !== EXCLUDED),
    },
  ]

  const renderTypeFilter = () => (
    <Select
      multiple
      value={documentTypes}
      onValueChange={(values) => table.setFilter("documentTypes", values)}
    >
      <SelectTrigger
        variant="button"
        className="w-full sm:w-auto"
        aria-label="Type"
      >
        Type
      </SelectTrigger>
      <SelectContent className="max-h-none">
        {DOCUMENT_TYPE_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const renderStatusFilter = () => (
    <Select
      multiple
      value={syncStatuses}
      onValueChange={(values) => table.setFilter("syncStatuses", values)}
    >
      <SelectTrigger
        variant="button"
        className="w-full sm:w-auto"
        aria-label="Status"
      >
        Status
      </SelectTrigger>
      <SelectContent className="max-h-none">
        {SYNC_STATUS_FILTER_OPTIONS.map((status) => (
          <SelectItem key={status} value={status}>
            {statusOptionLabel(status, table.data?.filterOptions)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const renderErrorCodeFilter = () => (
    <Select
      multiple
      value={syncErrorCodes}
      onValueChange={(values) => table.setFilter("syncErrorCodes", values)}
    >
      <SelectTrigger
        variant="button"
        className="w-full whitespace-nowrap sm:w-auto"
        aria-label="Error Code"
      >
        Error Code
      </SelectTrigger>
      <SelectContent className="max-h-none">
        {SYNC_ERROR_CODE_FILTER_OPTIONS.map((code) => (
          <SelectItem key={code} value={code}>
            {code}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const renderFilterChips = () => (
    <div className="flex flex-wrap">
      {table.filters.search && (
        <Chips.Chip
          text={`Search: ${table.filters.search}`}
          onClick={clearSearch}
        />
      )}
      {documentTypes.map((documentType) => (
        <Chips.Chip
          key={documentType}
          text={`Type: ${documentTypeLabel(documentType)}`}
          onClick={() =>
            table.setFilter(
              "documentTypes",
              documentTypes.filter((current) => current !== documentType)
            )
          }
        />
      ))}
      {syncStatuses.map((status) => (
        <Chips.Chip
          key={status}
          text={`Status: ${titlecase(status)}`}
          onClick={() =>
            table.setFilter(
              "syncStatuses",
              syncStatuses.filter((current) => current !== status)
            )
          }
        />
      ))}
      {syncErrorCodes.map((code) => (
        <Chips.Chip
          key={code}
          text={`Error code: ${code}`}
          onClick={() =>
            table.setFilter(
              "syncErrorCodes",
              syncErrorCodes.filter((current) => current !== code)
            )
          }
        />
      ))}
      {table.activeFilters.length > 0 && (
        <div className="flex w-min items-center">
          <Button variant="ghost" small onClick={resetFilters}>
            Reset
          </Button>
        </div>
      )}
    </div>
  )

  const renderRecordCount = () => {
    if (totalCount === undefined || filteredCount === undefined) return null

    return (
      <div className="text-muted text-sm">
        {`Showing ${filteredCount} of ${totalCount} ${pluralize(
          "record",
          totalCount
        )}`}
      </div>
    )
  }

  const renderEmptyState = (variant) => {
    let title
    let subtitle

    if (variant === "incompleteSetup") {
      title = "Syncing not yet started"
      subtitle =
        "Please complete setup and ensure your integration status is 'on'."
    } else if (variant === "error") {
      title = "Error loading records"
      subtitle =
        "Please try reloading the page or contact Dockwa support at mayday@dockwa.com"
    }

    return (
      <div className="card my-5 flex min-h-96 flex-col justify-center text-center">
        <div
          className={classNames(
            { "text-red-600": variant === "error" },
            "font-bold"
          )}
        >
          {title}
        </div>
        <div className="text-muted text-sm">{subtitle}</div>
        {variant === "incompleteSetup" ? (
          <div className="mt-2">
            <Button variant="primary" onClick={() => navigate("/setup")}>
              Go to Setup
            </Button>
          </div>
        ) : null}
      </div>
    )
  }

  const renderTable = () => {
    if (!integration.setupComplete) return renderEmptyState("incompleteSetup")
    if (table.isError) return renderEmptyState("error")

    return (
      <DataTableV2
        name="apideck-sync-details"
        columns={COLUMNS}
        data={table.records}
        actions={ACTIONS}
        maxHeight="none"
        emptyState={
          <span className="text-gray-500">
            {table.activeFilters.length > 0
              ? "There are no records that match your filters, please try adjusting them."
              : "There are no synced records available for review at this time."}
          </span>
        }
        {...table.tableProps}
      />
    )
  }

  return (
    <div className="mb-[250px]">
      <h4 className="mb-1 pt-6 font-semibold text-gray-900">Sync Details</h4>
      <div className="mb-8">
        <span className="text-gray-800">Log of recent sync activity.</span>
      </div>
      <div className="flex flex-wrap gap-2 xl:flex-nowrap">
        <div className="w-full pb-2 xl:w-[610px] xl:p-0">
          <Form.IconTextField
            icon={
              <i className="fa-solid fa-magnifying-glass text-xl text-gray-400" />
            }
            id="sync-record-search"
            isClearable
            onChange={handleSearch}
            onClearSelection={clearSearch}
            position="left"
            value={searchTerm}
            placeholder={`Search by customer, Dockwa ID, or ${
              serviceName || "accounting"
            } ref no.`}
          />
        </div>
        <div className="flex w-full gap-2 xl:w-min">
          {renderTypeFilter()}
          {renderStatusFilter()}
          {renderErrorCodeFilter()}
        </div>
      </div>
      <div className="px-4 xl:px-0">
        <Chips.List>{renderFilterChips()}</Chips.List>
      </div>
      <div className="mt-4 px-4 xl:px-0">{renderRecordCount()}</div>
      <div className="my-4">{renderTable()}</div>
    </div>
  )
}

export default SyncTab
